import { Steps, Tooltip } from 'antd'
import { type FC } from 'react'

import { Button, ModalBody, ModalFooter } from '@/components/elements'
import {
  ACTIONS,
  SHIPMENT_ORDER_CSV_IMPORT
} from '@/constants/common/message.constants'

import { isErrorRow } from '../../models'
import { DoneView } from '../DoneView'
import { FileView } from '../FileView'
import { MappingView } from '../MappingView'
import { MethodView, type ImportHandoffOption } from '../MethodView'
import { PreviewView } from '../PreviewView'
import {
  SuntoryFileView,
  SuntoryPreviewView,
  type UseSuntoryImportResult
} from '../SuntoryImport'

import { type UseShipmentOrderCsvImportResult } from './useShipmentOrderCsvImport'

type Props = UseShipmentOrderCsvImportResult & {
  tenantKey: string
  onClose: () => void
  /** サントリー様の CSV の選択肢。選ぶと useSuntoryImport（suntory）の流れで取り込む。 */
  handoffs: readonly ImportHandoffOption[]
  selectedHandoffKey: string | null
  onSelectHandoff: (key: string) => void
  suntory: UseSuntoryImportResult
}

// サントリー様の CSV は「取り込み方法・ファイル選択 → 確認 → 実行」の 3 段
const SUNTORY_STEP_ITEMS = [
  { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_METHOD },
  { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_PREVIEW },
  { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_EXECUTE }
]

/**
 * 配送依頼 CSVインポートのウィザード本体（表示のみ）。
 * ロジックは useShipmentOrderCsvImport が持ち、このコンポーネントは
 * その返り値をそのまま描画する（frontend-coding.md §1: view は pure）。
 */
export const ShipmentOrderCsvImportModalContentView: FC<Props> = ({
  view,
  stepIndex,
  stepItems,
  onBack,
  tenantKey,
  method,
  savedFormats,
  selectedFormatId,
  selectedFormat,
  canLeaveMethod,
  onChangeMethod,
  onChangeFormatId,
  onDeleteFormat,
  parsed,
  readConfig,
  parseError,
  encodingOverride,
  effectiveTable,
  canLeaveFile,
  onChangeReadConfig,
  onSelectFile,
  onChangeEncoding,
  onLeaveFile,
  canDownloadTemplate,
  onDownloadTemplate,
  onDownloadCutOut,
  mapping,
  skippedFields,
  canLeaveMapping,
  leaveMappingBlockedReason,
  onChangeColumn,
  onToggleSkip,
  savedFormatCheck,
  onLeaveMapping,
  isGeneric,
  preview,
  masterCandidates,
  standardMissingRequiredFields,
  excludeErrors,
  saveFormat,
  canLeavePreview,
  serverCheckStatus,
  onRecheck,
  isExecuting,
  customKeys,
  onEditCell,
  onBulkFill,
  onChangeExcludeErrors,
  onChangeSaveFormat,
  onExecute,
  result,
  onClose,
  handoffs,
  selectedHandoffKey,
  onSelectHandoff,
  suntory
}) => {
  const isSuntory = selectedHandoffKey !== null
  const isSuntoryPreview = isSuntory && suntory.stage === 'preview'
  return (
    <>
      {/* 枠の高さは全ステップ共通（画面の高さいっぱい）。中身の量でボタンの位置が動かないようにする。 */}
      <ModalBody className="h-[calc(100vh-150px)] overflow-y-auto">
        <Steps
          size="small"
          current={isSuntory ? (isSuntoryPreview ? 1 : 0) : stepIndex[view]}
          className="mb-4"
          items={isSuntory ? SUNTORY_STEP_ITEMS : stepItems}
        />

        {/* 中身の少ないステップは幅 900px のまま中央に置く */}
        <div className="mx-auto max-w-[900px]">
          {view === 'method' && !isSuntoryPreview && (
            <MethodView
              method={method}
              onChangeMethod={onChangeMethod}
              handoffs={handoffs}
              selectedHandoffKey={selectedHandoffKey}
              onSelectHandoff={onSelectHandoff}
              savedFormats={savedFormats}
              selectedFormatId={selectedFormatId}
              onChangeFormatId={onChangeFormatId}
              onDeleteFormat={() => void onDeleteFormat()}
            />
          )}
          {/* 取り込み方法が決まったら、同じ画面の下にファイル選択（開始行・ファイルの中身）を出す */}
          {view === 'method' &&
            selectedHandoffKey === null &&
            canLeaveMethod && (
              <FileView
                parsed={parsed}
                readConfig={readConfig}
                onChangeReadConfig={onChangeReadConfig}
                onSelectFile={onSelectFile}
                parseError={parseError}
                encodingOverride={encodingOverride}
                onChangeEncoding={onChangeEncoding}
                simpleMode={method === 'standard'}
                canDownloadTemplate={canDownloadTemplate}
                onDownloadTemplate={onDownloadTemplate}
              />
            )}
          {/* サントリー様の CSV は既存と同じドロップ欄で選び、中身は確認画面で事前チェック API に読ませる */}
          {view === 'method' && isSuntory && !isSuntoryPreview && (
            <SuntoryFileView
              key={selectedHandoffKey}
              onChangeUploadFile={suntory.onChangeUploadFile}
              onSelectFile={suntory.onSelectFile}
            />
          )}
        </div>
        {view === 'method' && isSuntoryPreview && (
          <SuntoryPreviewView
            check={suntory.check}
            overrides={suntory.overrides}
            isRechecking={suntory.isRechecking}
            masterCandidates={masterCandidates}
            onEditCell={suntory.onEditCell}
            changeCount={suntory.changeCount}
            onDownloadChanges={suntory.onDownloadChanges}
            canDownloadImported={suntory.canDownloadImported}
            onDownloadImported={() => void suntory.onDownloadImported()}
          />
        )}
        {view === 'mapping' && (
          <MappingView
            effectiveTable={effectiveTable}
            readConfig={readConfig}
            mapping={mapping}
            onChangeColumn={onChangeColumn}
            appliedFormatName={
              method === 'saved' ? (selectedFormat?.name ?? null) : null
            }
            skippedFields={skippedFields}
            onToggleSkip={onToggleSkip}
            customKeys={customKeys}
            savedFormatCheck={savedFormatCheck}
          />
        )}
        {view === 'preview' && method !== null && (
          <PreviewView
            method={method}
            tenantKey={tenantKey}
            effectiveTable={effectiveTable}
            preview={preview}
            masterCandidates={masterCandidates}
            onEditCell={onEditCell}
            skippedFields={
              isGeneric ? skippedFields : standardMissingRequiredFields
            }
            onBulkFill={onBulkFill}
            excludeErrors={excludeErrors}
            onChangeExcludeErrors={onChangeExcludeErrors}
            saveFormat={saveFormat}
            onChangeSaveFormat={onChangeSaveFormat}
            serverCheckStatus={serverCheckStatus}
            onRecheck={onRecheck}
          />
        )}
        {/* 中身の少ないステップは幅 900px のまま中央に置く */}
        <div className="mx-auto max-w-[900px]">
          {view === 'done' && result && (
            <DoneView
              result={result}
              registeredRows={
                preview ? preview.rows.filter((r) => !isErrorRow(r)) : []
              }
              onDownloadCutOut={onDownloadCutOut}
              onClose={onClose}
            />
          )}
        </div>
      </ModalBody>

      {view !== 'done' && (
        <ModalFooter>
          {view === 'method' && (
            <>
              {isSuntoryPreview ? (
                <Button onClick={suntory.onBack}>
                  {SHIPMENT_ORDER_CSV_IMPORT.BACK_METHOD}
                </Button>
              ) : (
                <Button onClick={onClose}>{ACTIONS.CANCEL}</Button>
              )}
              {isSuntory && !isSuntoryPreview && (
                <Button
                  type="primary"
                  disabled={!suntory.canLeaveFile}
                  onClick={() => void suntory.onLeaveFile()}
                  data-testid="shipment-order-csv-import__suntory-next"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.NEXT_CONFIRM}
                </Button>
              )}
              {isSuntoryPreview && (
                <Button
                  type="primary"
                  disabled={!suntory.canExecute}
                  loading={suntory.isExecuting}
                  onClick={() => void suntory.onExecute()}
                  data-testid="shipment-order-csv-import__suntory-execute"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.EXECUTE_IMPORT}
                </Button>
              )}
              {!isSuntory && (
                <Button
                  type="primary"
                  disabled={!canLeaveMethod || !canLeaveFile}
                  onClick={onLeaveFile}
                  data-testid="shipment-order-csv-import__next-from-file"
                >
                  {method === null
                    ? SHIPMENT_ORDER_CSV_IMPORT.NEXT
                    : method === 'standard'
                      ? SHIPMENT_ORDER_CSV_IMPORT.NEXT_CONFIRM
                      : SHIPMENT_ORDER_CSV_IMPORT.NEXT_MAPPING}
                </Button>
              )}
            </>
          )}
          {view === 'mapping' && (
            <>
              <Button onClick={() => onBack('method')}>
                {SHIPMENT_ORDER_CSV_IMPORT.BACK_METHOD}
              </Button>
              {/* 必須項目がそろい、保存した列の対応の保存し直しを選ぶまで確認へ進めない（理由をツールチップで案内） */}
              <Tooltip title={leaveMappingBlockedReason ?? ''}>
                <Button
                  type="primary"
                  disabled={!canLeaveMapping}
                  onClick={onLeaveMapping}
                  data-testid="shipment-order-csv-import__next-preview"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.NEXT_PREVIEW}
                </Button>
              </Tooltip>
            </>
          )}
          {view === 'preview' && (
            <>
              <Button
                onClick={() =>
                  onBack(method === 'standard' ? 'method' : 'mapping')
                }
              >
                {method === 'standard'
                  ? SHIPMENT_ORDER_CSV_IMPORT.BACK_METHOD
                  : SHIPMENT_ORDER_CSV_IMPORT.BACK_MAPPING}
              </Button>
              {/* 送れない状態（不一致警告・ファイル全体の問題・除外しないエラー行・取り込める行が無い）では押せない */}
              <Button
                type="primary"
                disabled={!canLeavePreview}
                loading={isExecuting}
                onClick={() => void onExecute()}
                data-testid="shipment-order-csv-import__execute"
              >
                {SHIPMENT_ORDER_CSV_IMPORT.EXECUTE_IMPORT}
              </Button>
            </>
          )}
        </ModalFooter>
      )}
    </>
  )
}
